/*
 * THE auth-source seam for my-api calls (help-desk assisted session,
 * DESIGN-HELPDESK-KNOWN-TICKET-ASSISTED-COMPLETION-V1; LOCKED contract
 * HELPDESK-CONTRACT-LOCKED-1102).
 *
 *   authorizationFor(idToken) → `Bearer <idToken>`   normally (byte-identical
 *                                                     to the old hand-built
 *                                                     header), or
 *                             → `Assist <as1>`       while an assisted
 *                                                     session is active.
 *
 * The assisted session lives IN MEMORY ONLY (this module's variable). Entering
 * assisted mode first swaps localStorage / sessionStorage for empty in-memory
 * Storage objects for the rest of the page's life, so on the shared help-desk
 * tablet nothing is written to real storage and nothing a previous person left
 * there (drafts, a waiver copy, a session) is read. If the swap is impossible
 * the session is refused (fail closed).
 *
 * Under assist only my-api is called: settings-direct and tickets-service
 * calls are skipped by their modules (assistedSession() !== null), so the as1
 * session never leaves for another service and no Cognito call is made.
 */
import type { AssistSession } from '../data/assist'

let assisted: AssistSession | null = null
let ended: { complete: boolean } | null = null
const listeners = new Set<() => void>()

/** The placeholder idToken carried by the synthetic in-memory Session while
 *  assisted (session.ts). It is never sent: authorizationFor ignores it. */
export const ASSISTED_ID_TOKEN = 'assisted-session'

export function assistedSession(): AssistSession | null {
  return assisted
}

export function authorizationFor(idToken: string): string {
  return assisted ? `Assist ${assisted.session}` : `Bearer ${idToken}`
}

export function subscribeAssisted(cb: () => void): () => void {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

class MemoryStorage implements Storage {
  private m = new Map<string, string>()
  get length(): number {
    return this.m.size
  }
  clear(): void {
    this.m.clear()
  }
  getItem(k: string): string | null {
    return this.m.has(k) ? (this.m.get(k) as string) : null
  }
  key(i: number): string | null {
    return [...this.m.keys()][i] ?? null
  }
  removeItem(k: string): void {
    this.m.delete(k)
  }
  setItem(k: string, v: string): void {
    this.m.set(k, String(v))
  }
}

/** Shadow window.localStorage / sessionStorage with empty memory Storage.
 *  Returns false when the browser refuses (the caller then fails closed). */
export function installMemoryStorage(win: Window & typeof globalThis = window): boolean {
  try {
    const local = new MemoryStorage()
    const session = new MemoryStorage()
    Object.defineProperty(win, 'localStorage', { value: local, configurable: true, writable: false })
    Object.defineProperty(win, 'sessionStorage', { value: session, configurable: true, writable: false })
    return win.localStorage === local && win.sessionStorage === session
  } catch {
    return false
  }
}

/** Enter assisted mode: memory-only storage first, then the session. */
export function enterAssistedMode(s: AssistSession, win: Window & typeof globalThis = window): boolean {
  if (!installMemoryStorage(win)) return false
  assisted = s
  for (const l of [...listeners]) l()
  return true
}

/** End the assisted session (return / done): the as1 session is DROPPED from
 *  memory and the page stays on the assisted done screen (never falls back to
 *  the sign-in or app shell). Memory storage stays for the page's life. */
export function endAssistedMode(complete: boolean): void {
  assisted = null
  ended = { complete }
  for (const l of [...listeners]) l()
}

/** Non-null once the assisted session ended on this page. */
export function assistEnded(): { complete: boolean } | null {
  return ended
}

/** Test seam. */
export function resetAssistedForTests(): void {
  assisted = null
  ended = null
}
